/**
 * Core-100 R3 (OD-27) — OptiBay staff restore a deactivated account before its personal information is
 * purged. The person is signed out while deactivated, so restoring cannot be self-service. Every restore
 * is made by `restore_deactivated_account`, which requires an internal admin and a stated basis, refuses
 * once the purge has run, and writes ACCOUNT_REACTIVATED to the audit log. Shop memberships are NOT
 * restored — a shop re-invites the person.
 */
import { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface DeactivatedAccountRow {
  user_id: string;
  deactivated_at: string | null;
  scheduled_for_pii_purge_at: string | null;
}

export function describeRestoreError(message: string | undefined): string {
  if (!message) return 'The account was not restored.';
  if (message.includes('ACCOUNT_ALREADY_PURGED')) return 'Personal information was already removed; this account cannot be restored.';
  if (message.includes('ACCOUNT_NOT_DEACTIVATED')) return 'This account is not deactivated.';
  if (message.includes('RESTORE_BASIS_REQUIRED')) return 'Say why the account is being restored.';
  if (message.includes('INTERNAL_ADMIN_REQUIRED')) return 'Only OptiBay staff can restore an account.';
  return 'The account was not restored.';
}

const day = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString() : 'unknown');

export function DeactivatedAccountsCard() {
  const [rows, setRows] = useState<DeactivatedAccountRow[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [basis, setBasis] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    const { data, error } = await supabase.rpc('deactivated_accounts_pending_purge' as never);
    if (error) { setFailed(true); setRows(null); return; }
    setFailed(false);
    setRows(((data ?? []) as unknown) as DeactivatedAccountRow[]);
  }, []);
  useEffect(() => { void load(); }, [load]);

  const restore = async (userId: string) => {
    const reason = (basis[userId] ?? '').trim();
    if (reason.length < 5) { toast.error('Say why the account is being restored'); return; }
    setBusy(userId);
    const { error } = await supabase.rpc('restore_deactivated_account' as never, { p_user: userId, p_basis: reason } as never);
    setBusy(null);
    if (error) { toast.error(describeRestoreError(error.message)); return; }
    toast.success('Account restored. Shop memberships were not restored — shops re-invite the person.');
    void load();
  };

  return (
    <Card data-testid="deactivated-accounts">
      <CardHeader>
        <CardTitle>Deactivated accounts</CardTitle>
        <CardDescription>
          Accounts that can still be restored, before their personal information is removed.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {failed && <p className="text-sm text-destructive">Deactivated accounts could not be loaded.</p>}
        {!failed && rows === null && <p className="text-sm text-muted-foreground">Loading…</p>}
        {!failed && rows !== null && rows.length === 0 && (
          <p className="text-sm text-muted-foreground">No account is waiting to be purged.</p>
        )}
        {rows?.map((r) => (
          <div key={r.user_id} className="space-y-2 rounded-md border p-3" data-testid="deactivated-account">
            <p className="text-sm">
              Account <span className="font-mono">{r.user_id.slice(0, 8)}</span> — deactivated {day(r.deactivated_at)};
              personal information is removed on {day(r.scheduled_for_pii_purge_at)}.
            </p>
            <div className="flex flex-col gap-2 sm:flex-row">
              <Input
                aria-label="Reason for restoring"
                placeholder="Why is this account being restored?"
                value={basis[r.user_id] ?? ''}
                onChange={(e) => setBasis((b) => ({ ...b, [r.user_id]: e.target.value }))}
              />
              <Button disabled={busy === r.user_id} onClick={() => void restore(r.user_id)}>Restore</Button>
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
